/**
 * File backstage — New / Open / Recent / Import / Export / Preferences.
 *
 * Ribbon File buttons deep-link here via `fileSection` in the results store.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Download,
  FilePlus2,
  FolderOpen,
  Save,
  Settings,
} from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Select,
} from "@/components/ui/primitives";
import {
  listRecentCases,
  openCase,
  pickDirectory,
  splitError,
} from "@/ipc/api";
import type { PipelineKind, RecentCase } from "@/ipc/contract";
import { formatDateTime } from "@/lib/utils";
import { dispatchAction } from "@/shell/actions";
import { goToTab, showCaseInViewer } from "@/pipeline/actions";
import { usePipelineSession } from "@/pipeline/session";
import { ExportPanel } from "@/panels/ExportPanel";
import { type FileSection, useResultsStore } from "@/results/state";
import { NoticeBar } from "@/results/ui";
import { appActions, usePreferences } from "@/store/appStore";

const SECTIONS: { id: FileSection; label: string }[] = [
  { id: "new", label: "New" },
  { id: "open", label: "Open" },
  { id: "recent", label: "Recent" },
  { id: "import", label: "Import" },
  { id: "export", label: "Export" },
  { id: "preferences", label: "Preferences" },
];

export function FileTab() {
  const section = useResultsStore((s) => s.fileSection);
  const setSection = useResultsStore((s) => s.setFileSection);
  const notice = useResultsStore((s) => s.notice);
  const setNotice = useResultsStore((s) => s.setNotice);

  return (
    <div className="flex h-full min-h-0 bg-ads-bg">
      <nav className="w-44 shrink-0 border-r border-ads-border bg-ads-surface py-2">
        {SECTIONS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSection(item.id)}
            className={`block w-full px-4 py-1.5 text-left text-sm ${
              section === item.id ? "bg-ads-accent-soft font-medium text-ads-accent" : "text-ads-text hover:bg-ads-hover"
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>
      <div className="min-w-0 flex-1 overflow-auto p-4">
        {notice ? <NoticeBar message={notice} onDismiss={() => setNotice(null)} /> : null}
        {section === "new" ? <NewSection /> : null}
        {section === "open" || section === "recent" ? <OpenSection /> : null}
        {section === "import" ? <ImportSection /> : null}
        {section === "export" ? <ExportPanel /> : null}
        {section === "preferences" ? <PreferencesSection /> : null}
        {section === "save" || section === "saveAs" ? <SaveSection /> : null}
        {section === "close" || section === "exit" ? <OpenSection /> : null}
      </div>
    </div>
  );
}

function NewSection() {
  const start = (kind: PipelineKind, preset: "full" | "blank") => {
    usePipelineSession.getState().startNewAnalysis(kind, preset);
    goToTab("pipeline");
  };
  return (
    <div className="grid max-w-3xl grid-cols-2 gap-2">
      <LaunchCard
        title="DWI Pipeline"
        hint="6 stages · stroke core"
        onClick={() => start("dwi", "full")}
        icon={<FilePlus2 className="h-5 w-5 text-ads-accent" />}
      />
      <LaunchCard
        title="PWI Pipeline"
        hint="8 stages · hypoperfusion"
        onClick={() => start("pwi", "full")}
        icon={<FilePlus2 className="h-5 w-5 text-ads-accent" />}
      />
      <LaunchCard
        title="DWI + PWI Combined"
        hint="Both halves, one case"
        onClick={() => start("combined", "full")}
        icon={<FilePlus2 className="h-5 w-5 text-ads-accent" />}
      />
      <LaunchCard
        title="Blank"
        hint="Pick stages yourself"
        onClick={() => start("dwi", "blank")}
        icon={<FilePlus2 className="h-5 w-5 text-ads-accent" />}
      />
    </div>
  );
}

function LaunchCard({
  title,
  hint,
  onClick,
  icon,
}: {
  title: string;
  hint: string;
  onClick: () => void;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-1 rounded border border-ads-border bg-ads-surface px-4 py-4 text-left hover:border-ads-accent-border hover:bg-ads-accent-soft"
    >
      {icon}
      <span className="text-sm font-semibold text-ads-text">{title}</span>
      <span className="text-[11px] text-ads-muted">{hint}</span>
    </button>
  );
}

function OpenSection() {
  const [cases, setCases] = useState<RecentCase[]>([]);

  useEffect(() => {
    void listRecentCases()
      .then(setCases)
      .catch(() => setCases([]));
  }, []);

  return (
    <div className="max-w-3xl space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Open a subject or case folder</CardTitle>
        </CardHeader>
        <CardBody>
          <Button type="button" onClick={() => dispatchAction("home.openCase")}>
            <FolderOpen className="h-3.5 w-3.5" />
            Open Subject…
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Recent</CardTitle>
        </CardHeader>
        {cases.length === 0 ? (
          <EmptyState title="No recent cases" />
        ) : (
          <CardBody className="space-y-1 px-0 py-1">
            {cases.map((item) => (
              <button
                key={item.caseId}
                type="button"
                className="flex w-full items-center justify-between px-4 py-1.5 text-left text-sm hover:bg-neutral-50"
                onClick={async () => {
                  try {
                    await showCaseInViewer(await openCase(item.caseJsonPath));
                  } catch (error) {
                    useResultsStore.getState().setNotice(splitError(error).message);
                  }
                }}
              >
                <span>
                  <span className="font-medium text-neutral-800">{item.subjectId}</span>
                  <span className="ml-2 text-[11px] text-neutral-400">{item.studyName || item.modality}</span>
                </span>
                <span className="text-[11px] text-neutral-400">{formatDateTime(item.lastModifiedIso)}</span>
              </button>
            ))}
          </CardBody>
        )}
      </Card>
    </div>
  );
}

function ImportSection() {
  return (
    <div className="grid max-w-2xl gap-2 sm:grid-cols-2">
      <LaunchCard
        title="Import subject folder"
        hint="Scan a folder of NIfTI files and open them in the viewer."
        icon={<Download className="h-5 w-5 text-ads-accent" />}
        onClick={() => dispatchAction("home.importData")}
      />
      <LaunchCard
        title="Import NIfTI"
        hint="Pick .nii / .nii.gz files; the parent folder becomes the subject."
        icon={<Download className="h-5 w-5 text-ads-accent" />}
        onClick={() => dispatchAction("import.nifti")}
      />
      <LaunchCard
        title="Import mask"
        hint="Requires an open case, or starts a new analysis from the file's folder."
        icon={<Download className="h-5 w-5 text-ads-accent" />}
        onClick={() => dispatchAction("import.mask")}
      />
    </div>
  );
}

function SaveSection() {
  return (
    <div className="flex max-w-md flex-col gap-2">
      <Button type="button" onClick={() => dispatchAction("file.saveCase")}>
        <Save className="h-3.5 w-3.5" />
        Save Case
      </Button>
      <Button type="button" onClick={() => dispatchAction("case.saveAs")}>
        Save Case As…
      </Button>
    </div>
  );
}

function PreferencesSection() {
  const prefs = usePreferences();
  const [inputRoot, setInputRoot] = useState(prefs?.defaultInputRoot ?? "");
  const [outputRoot, setOutputRoot] = useState(prefs?.defaultOutputRoot ?? "");
  const [gpu, setGpu] = useState(prefs?.defaultGpuIndex ?? "0");
  const [cleanup, setCleanup] = useState(prefs?.cleanupEnabled ?? true);

  useEffect(() => {
    if (!prefs) return;
    setInputRoot(prefs.defaultInputRoot ?? "");
    setOutputRoot(prefs.defaultOutputRoot ?? "");
    setGpu(prefs.defaultGpuIndex);
    setCleanup(prefs.cleanupEnabled);
  }, [prefs]);

  const browse = useCallback(async (which: "input" | "output") => {
    const { path } = await pickDirectory(
      which === "input" ? "Default input folder" : "Default output folder",
      which === "input" ? inputRoot : outputRoot,
    );
    if (!path) return;
    if (which === "input") setInputRoot(path);
    else setOutputRoot(path);
  }, [inputRoot, outputRoot]);

  const save = async () => {
    await appActions.updatePreferences({
      defaultInputRoot: inputRoot || null,
      defaultOutputRoot: outputRoot || null,
      defaultGpuIndex: gpu,
      cleanupEnabled: cleanup,
    });
    useResultsStore.getState().setNotice("Preferences saved.");
  };

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-neutral-400" />
          Default folders
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <Field label="Default input folder">
          <div className="flex gap-1.5">
            <Input value={inputRoot} onChange={(e) => setInputRoot(e.target.value)} />
            <Button type="button" onClick={() => void browse("input")}>
              Browse
            </Button>
          </div>
        </Field>
        <Field label="Default output folder">
          <div className="flex gap-1.5">
            <Input value={outputRoot} onChange={(e) => setOutputRoot(e.target.value)} />
            <Button type="button" onClick={() => void browse("output")}>
              Browse
            </Button>
          </div>
        </Field>
        <Field label="Default GPU index">
          <Select value={gpu} onChange={(e) => setGpu(e.target.value)}>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="cpu">CPU</option>
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-xs text-neutral-700">
          <Checkbox checked={cleanup} onChange={(e) => setCleanup(e.target.checked)} />
          Clean up intermediate files after a job
        </label>
        <Button type="button" variant="primary" onClick={() => void save()}>
          Save preferences
        </Button>
      </CardBody>
    </Card>
  );
}

export default FileTab;
